import { useEffect, useMemo } from 'react';
import { useAppState } from '../context/useAppState';
import {
  areNotairesSalariesEqual,
  buildNotairesVirtualSalaries,
  NOTAIRE_AUTO_ID_PREFIX,
} from '../lib/notairesEngine';

/**
 * Hook de synchronisation du module Notaires avec la table `state.salaries`.
 *
 * À chaque changement de :
 *   - `state.notairesConfig` (paramètres)
 *   - `state.revenus` (change le CA projeté → change le plan de recrutement)
 *   - `state.meta.exerciceXDuree` (change le nombre total de mois de projection)
 *
 * on recalcule la liste de salariés virtuels attendue et on dispatche `NOTAIRES_SALARIES_SYNC`
 * UNIQUEMENT si elle diffère de la liste actuellement persistée dans `state.salaries`.
 *
 * Cette garde d'égalité est indispensable pour :
 *   - éviter la boucle infinie (dispatch → re-render → re-calcul → dispatch),
 *   - limiter les écritures auto-save à ce qui est strictement nécessaire.
 *
 * Les lignes générées sont identifiées par le préfixe d'id `notaire-auto-`.
 * Les salariés saisis manuellement par l'utilisateur ne sont jamais modifiés.
 */
export function useNotairesSync() {
  const { state, dispatch } = useAppState();

  const expected = useMemo(() => buildNotairesVirtualSalaries(state), [
    // Ne ré-évalue que quand ces tranches changent réellement.
    state.notairesConfig,
    state.revenus,
    state.meta.exercice1Duree,
    state.meta.exercice2Duree,
    state.meta.exercice3Duree,
    state.meta.caScenarioPct,
  ]);

  const current = useMemo(
    () => state.salaries.filter((s) => s.id.startsWith(NOTAIRE_AUTO_ID_PREFIX)),
    [state.salaries],
  );

  useEffect(() => {
    if (!areNotairesSalariesEqual(current, expected)) {
      dispatch({ type: 'NOTAIRES_SALARIES_SYNC', salaries: expected });
    }
  }, [current, expected, dispatch]);
}
